import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FaEthereum } from 'react-icons/fa';
import { getPassportInstance } from '../../config/passport';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../config/api';

/**
 * NFTPurchaseButton Component
 *
 * This component provides a button for users to purchase and mint NFTs using Immutable Passport.
 * It implements lazy minting - the NFT is only minted when a user purchases it.
 */
export default function NFTPurchaseButton({ nft, onSuccess, className = '' }) {
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isPassportConnected, setIsPassportConnected] = useState(false);
  const { user, updateUserContext, isPassportUser } = useAuth();

  // Check if already connected with Passport
  useEffect(() => {
    if (user?.isConnected && isPassportUser()) {
      setIsPassportConnected(true);
    } else {
      setIsPassportConnected(false);
    }
  }, [user, isPassportUser]);

  const connectPassport = async () => {
    try {
      console.log('Connecting to Immutable Passport...');

      // Get Passport instance
      const passportInstance = getPassportInstance();
      if (!passportInstance) {
        throw new Error('Failed to initialize Passport. Please try again.');
      }

      // First, check if user is already logged in with Passport
      try {
        const userInfo = await passportInstance.getUserInfo();
        console.log('User already logged in with Passport:', userInfo);

        // If we have user info but no provider, connect the provider
        if (userInfo) {
          // Connect to Passport EVM provider
          const provider = await passportInstance.connectEvm();
          console.log('Connected to Passport EVM provider:', provider);

          // Get user's address
          const accounts = await provider.request({ method: 'eth_requestAccounts' });
          const address = accounts[0];
          console.log('Got wallet address from Passport:', address);

          // Update auth context with Passport user info
          updateUserContext({
            ...user,
            passportAddress: address,
            passportInfo: userInfo
          });

          setIsPassportConnected(true);

          return { provider, address, userInfo };
        }
      } catch (userInfoError) {
        console.log('User not logged in with Passport, proceeding with login flow');
      }

      // Connect to Passport
      const provider = await passportInstance.connectEvm();
      console.log('Connected to Passport EVM provider:', provider);

      // Get user's address
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      const address = accounts[0];
      console.log('Got wallet address from Passport:', address);

      // Get user info
      const userInfo = await passportInstance.getUserInfo();
      console.log('Got user info from Passport:', userInfo);

      // Update auth context with Passport user info
      updateUserContext({
        ...user,
        passportAddress: address,
        passportInfo: userInfo
      });

      setIsPassportConnected(true);

      return { provider, address, userInfo };
    } catch (error) {
      console.error('Error connecting to Passport:', error);
      if (error.message) console.error('Error message:', error.message);
      if (error.stack) console.error('Error stack:', error.stack);
      toast.error('Failed to connect to Immutable Passport');
      throw error;
    }
  };

  // Function to refresh the authentication token
  const refreshAuthToken = async () => {
    try {
      console.log('Attempting to refresh authentication token...');

      // Get the current user from localStorage
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      const token = localStorage.getItem('token');

      // Check if we're using Passport authentication
      const isUsingPassport = isPassportUser();
      console.log('Is using Passport for authentication:', isUsingPassport);

      if (isUsingPassport && user?.passportInfo) {
        console.log('Using Passport authentication, refreshing Passport connection');

        try {
          // For Passport users, reconnect to ensure we have a fresh session
          const passportInstance = getPassportInstance();
          if (passportInstance) {
            // Try to get fresh user info
            const userInfo = await passportInstance.getUserInfo();
            console.log('Refreshed Passport user info:', userInfo);

            // Connect to EVM provider
            const provider = await passportInstance.connectEvm();
            const accounts = await provider.request({ method: 'eth_requestAccounts' });
            const address = accounts[0];

            // Update auth context with refreshed Passport info
            const updatedUser = {
              ...user,
              passportAddress: address,
              passportInfo: userInfo,
              isConnected: true
            };

            // Update context and localStorage
            updateUserContext(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // For Passport users, we don't need to refresh the token via API
            // as the Passport session is handled separately
            return true;
          }
        } catch (passportError) {
          console.error('Error refreshing Passport connection:', passportError);
          // Continue with standard token refresh as fallback
        }
      }

      // Standard token refresh via API
      if (!currentUser) {
        console.error('No user data found in localStorage');
        return false;
      }

      // Check if we have a valid token to use for the refresh request
      if (!token) {
        console.error('No token found in localStorage');
        return false;
      }

      console.log('Attempting to refresh token via API');

      // Ensure token has Bearer prefix for the API call
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

      // Try to get a fresh token from the API with explicit Authorization header
      const response = await api.get(API_ENDPOINTS.auth.refreshToken, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response && response.token) {
        console.log('Successfully refreshed token via API');

        // Get clean token without Bearer prefix for storage
        const cleanToken = response.token.startsWith('Bearer ')
          ? response.token.substring(7).trim()
          : response.token.trim();

        // Update the token in localStorage
        localStorage.setItem('token', cleanToken);

        // Update the user object with the new token
        const updatedUser = {
          ...currentUser,
          token: cleanToken
        };

        localStorage.setItem('user', JSON.stringify(updatedUser));

        // Update the auth context
        if (updateUserContext) {
          updateUserContext(updatedUser);
        }

        return true;
      } else {
        console.error('Failed to refresh token:', response);
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  const handlePurchase = async () => {
    if (isPurchasing) return;

    setIsPurchasing(true);
    const loadingToastId = toast.loading('Preparing to purchase NFT...');

    try {
      // First, clear any existing token to force a fresh authentication
      // This helps prevent using stale tokens that might be rejected
      console.log('Clearing existing authentication tokens to start fresh');
      localStorage.removeItem('token');

      // Connect to Passport if not already connected
      let address = user?.passportAddress;
      console.log('Current user:', user);
      console.log('Current passport address:', address);

      // Always reconnect to Passport to ensure we have a fresh session
      console.log('Connecting to Passport to ensure fresh session...');
      try {
        const connection = await connectPassport();
        address = connection.address;
        console.log('Connected to Passport with address:', address);
        toast.loading('Connected to Immutable Passport', { id: loadingToastId });

        // Explicitly set connected state
        setIsPassportConnected(true);
      } catch (connectError) {
        console.error('Error connecting to Passport:', connectError);
        toast.error(`Failed to connect to Immutable Passport: ${connectError.message || 'Unknown error'}`, { id: loadingToastId });
        setIsPurchasing(false);
        return;
      }

      if (!address) {
        console.error('No wallet address available after connection');
        toast.error('Failed to get wallet address', { id: loadingToastId });
        setIsPurchasing(false);
        return;
      }

      // Verify NFT data before purchase
      if (!nft || !nft._id) {
        console.error('Invalid NFT data:', nft);
        toast.error('Invalid NFT data. Please try again with a valid NFT.', { id: loadingToastId });
        setIsPurchasing(false);
        return;
      }

      // Get a JWT token from the server specifically for this purchase
      console.log('Getting fresh JWT token for purchase...');
      try {
        // Make a request to get a fresh JWT token
        const tokenResponse = await api.get(`/api/auth/passport-token?address=${address}`, {
          headers: {
            'Content-Type': 'application/json',
            // Don't include Authorization header to avoid using an expired token
          }
        });

        if (tokenResponse && tokenResponse.token) {
          console.log('Received fresh JWT token from server:', tokenResponse);

          // Make sure the token is in the correct format (JWT tokens start with "ey")
          if (tokenResponse.token.startsWith('ey') || tokenResponse.token.startsWith('Bearer ey')) {
            // Store the token without Bearer prefix
            const cleanToken = tokenResponse.token.startsWith('Bearer ')
              ? tokenResponse.token.substring(7)
              : tokenResponse.token;

            console.log('Storing clean JWT token:', cleanToken.substring(0, 20) + '...');
            localStorage.setItem('token', cleanToken);

            // Update user context with the new token and any user info
            const updatedUser = {
              ...user,
              token: cleanToken
            };

            // If the response includes user info, add it to the context
            if (tokenResponse.user) {
              updatedUser.id = tokenResponse.user.id;
              updatedUser.username = tokenResponse.user.username;
              updatedUser.isAdmin = tokenResponse.user.isAdmin;
            }

            updateUserContext(updatedUser);
            console.log('Updated user context with new token and user info');
          } else {
            console.error('Received token is not in JWT format:', tokenResponse.token.substring(0, 10));
          }
        } else {
          console.warn('Did not receive a token from server, proceeding with Passport authentication only');
        }
      } catch (tokenError) {
        console.error('Error getting fresh token:', tokenError);
        // Continue anyway, as we'll use Passport authentication
      }

      // Call the purchase endpoint
      toast.loading('Processing your purchase...', { id: loadingToastId });
      console.log('Purchasing NFT with ID:', nft._id, 'for address:', address);
      console.log('Purchase endpoint:', API_ENDPOINTS.nft.purchase);

      try {
        // Get the token and user info
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        const userData = userStr ? JSON.parse(userStr) : null;

        // Get Passport instance to get fresh ID token
        const passportInstance = getPassportInstance();
        let passportIdToken = null;

        try {
          // Get fresh user info from Passport
          const userInfo = await passportInstance.getUserInfo();
          if (userInfo && userInfo.idToken) {
            passportIdToken = userInfo.idToken;
            console.log('Got fresh Passport ID token');
          }
        } catch (idTokenError) {
          console.error('Error getting Passport ID token:', idTokenError);
        }

        console.log('Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'null');
        console.log('Have Passport ID token:', !!passportIdToken);

        // Create request headers with JWT token as primary authentication
        const headers = {
          'Content-Type': 'application/json',
          'X-Debug-Token': 'true'
        };

        // Add JWT token - this is the primary authentication method
        if (token) {
          // Make sure token has Bearer prefix for JWT authentication
          const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
          headers['Authorization'] = authToken;
          console.log('Added JWT token to request:', authToken.substring(0, 20) + '...');
        } else {
          console.warn('No JWT token available for purchase request');
        }

        // Add additional context information
        headers['X-Wallet-Address'] = address;

        // Add user ID if available
        if (userData && userData._id) {
          headers['X-User-ID'] = userData._id;
        }

        // Log the headers we're sending (without sensitive values)
        console.log('Sending request with headers:', Object.keys(headers));

        // Send purchase request with JWT authentication
        console.log('Sending purchase request with JWT authentication');
        const purchaseResponse = await api.post(API_ENDPOINTS.nft.purchase, {
          nftId: nft._id,
          buyerAddress: address
        }, { headers });

        console.log('Purchase response:', purchaseResponse);

        // Check if the purchase was successful
        if (!purchaseResponse) {
          console.error('Empty purchase response');
          toast.error('Failed to purchase NFT: Empty response from server', { id: loadingToastId });
          setIsPurchasing(false);
          return;
        }

        // Check if there's an error in the response
        if (purchaseResponse.error) {
          console.error('Error in purchase response:', purchaseResponse.error);
          toast.error(`Failed to purchase NFT: ${purchaseResponse.error}`, { id: loadingToastId });
          setIsPurchasing(false);
          return;
        }

        toast.success('Purchase initiated! Your NFT is being minted.', { id: loadingToastId });

        // Poll for minting status
        pollMintStatus(nft._id, loadingToastId);
      } catch (purchaseError) {
        console.error('Error in purchase API call:', purchaseError);
        console.error('Error details:', purchaseError.response || purchaseError);

        // Extract detailed error information
        const errorData = purchaseError.data || {};
        const errorCode = errorData.code || '';
        const errorDetails = errorData.details || {};

        console.log('Error code:', errorCode);
        console.log('Error details:', errorDetails);

        // Handle specific error cases
        if (purchaseError.status === 403) {
          if (errorCode === 'INVALID_TOKEN') {
            console.log('Invalid token error detected, attempting to reconnect...');

            // Clear token and try to reconnect
            localStorage.removeItem('token');

            // Show specific message to the user
            toast.error('Your session has expired. Please try again to reconnect.', { id: loadingToastId });

            // Force reconnection on next attempt
            setIsPassportConnected(false);
          } else {
            toast.error('Authentication failed. Please log in again.', { id: loadingToastId });
          }
        } else if (purchaseError.status === 400) {
          // Handle validation errors
          const errorMessage = errorData.message || purchaseError.message || 'Invalid request';
          toast.error(`Failed to purchase NFT: ${errorMessage}`, { id: loadingToastId });
        } else if (purchaseError.status === 404) {
          toast.error('NFT not found or no longer available for purchase.', { id: loadingToastId });
        } else {
          toast.error(`Failed to purchase NFT: ${purchaseError.message || 'Server error'}`, { id: loadingToastId });
        }

        setIsPurchasing(false);
      }
    } catch (error) {
      console.error('Error purchasing NFT:', error);
      toast.error(`Failed to purchase NFT: ${error.message || 'Unknown error'}`, { id: loadingToastId });
      setIsPurchasing(false);
    }
  };

  // Poll for minting status
  const pollMintStatus = async (nftId, toastId) => {
    try {
      let attempts = 0;
      const maxAttempts = 20;
      const pollInterval = 5000; // 5 seconds

      // Set initial purchase stage
      setPurchaseStage('payment');

      const checkStatus = async () => {
        attempts++;

        try {
          // Get the latest NFT data and transaction data
          const response = await api.get(API_ENDPOINTS.nft.nft(nftId));
          console.log('NFT status response:', response);

          // Try to get transaction data if available
          let transactionData = null;
          try {
            const txResponse = await api.get(`${API_ENDPOINTS.nft.transactions}?nftId=${nftId}`);
            if (txResponse && txResponse.data && txResponse.data.length > 0) {
              // Get the most recent transaction
              transactionData = txResponse.data[0];
              console.log('Transaction data:', transactionData);

              // Update purchase stage based on transaction status
              if (transactionData.paymentStatus === 'pending') {
                setPurchaseStage('payment');
                toast.loading('Processing payment...', { id: toastId });
              } else if (transactionData.paymentStatus === 'completed' && transactionData.status === 'pending') {
                setPurchaseStage('minting');
                toast.loading('Payment confirmed! Minting your NFT...', { id: toastId });
              } else if (transactionData.status === 'completed') {
                setPurchaseStage('completed');
              } else if (transactionData.status === 'failed') {
                setPurchaseStage('failed');
                toast.error(`Purchase failed: ${transactionData.error || 'Unknown error'}`, { id: toastId });
                setIsPurchasing(false);
                return;
              }
            }
          } catch (txError) {
            console.error('Error fetching transaction data:', txError);
            // Continue with NFT status check even if transaction fetch fails
          }

          // Handle different response formats
          // The API might return the NFT directly or wrapped in a data property
          let updatedNft;

          if (response && response.data) {
            // Response has a data property
            updatedNft = response.data;
          } else if (response && response._id) {
            // Response is the NFT object directly
            updatedNft = response;
          } else {
            // Invalid response format
            console.error('Invalid response format:', response);
            if (attempts < maxAttempts) {
              toast.loading(`Waiting for NFT data (${attempts}/${maxAttempts})...`, { id: toastId });
              setTimeout(checkStatus, pollInterval);
              return;
            } else {
              toast.error('Failed to get NFT data after multiple attempts', { id: toastId });
              setIsPurchasing(false);
              return;
            }
          }

          // Check if the NFT has a status property
          if (!updatedNft.hasOwnProperty('status')) {
            console.error('NFT data does not have a status property:', updatedNft);
            if (attempts < maxAttempts) {
              toast.loading(`Waiting for NFT status (${attempts}/${maxAttempts})...`, { id: toastId });
              setTimeout(checkStatus, pollInterval);
              return;
            } else {
              toast.error('Failed to get NFT status after multiple attempts', { id: toastId });
              setIsPurchasing(false);
              return;
            }
          }

          if (updatedNft.status === 'minted') {
            // Minting successful
            toast.success('NFT purchased and minted successfully!', { id: toastId });
            setPurchaseStage('completed');
            setIsPurchasing(false);
            if (onSuccess) onSuccess(updatedNft);
            return;
          } else if (updatedNft.status === 'draft' && transactionData && transactionData.status === 'failed') {
            // Minting failed
            toast.error(`NFT purchase failed: ${transactionData.error || 'Please try again.'}`, { id: toastId });
            setPurchaseStage('failed');
            setIsPurchasing(false);
            return;
          } else if (updatedNft.status === 'minting') {
            // Still minting
            setPurchaseStage('minting');
            toast.loading(`Minting in progress (${attempts}/${maxAttempts})...`, { id: toastId });
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, pollInterval);
            } else {
              toast.loading('Minting is taking longer than expected. Please check back later.', { id: toastId });
              setIsPurchasing(false);
            }
          } else if (attempts < maxAttempts) {
            // Status is something else, keep checking
            toast.loading(`Processing purchase (${attempts}/${maxAttempts})...`, { id: toastId });
            setTimeout(checkStatus, pollInterval);
          } else {
            // Timeout
            toast.loading('Purchase is taking longer than expected. Please check back later.', { id: toastId });
            setIsPurchasing(false);
          }
        } catch (error) {
          console.error('Error checking mint status:', error);
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, pollInterval);
          } else {
            toast.error('Failed to check purchase status', { id: toastId });
            setIsPurchasing(false);
          }
        }
      };

      // Start checking
      setTimeout(checkStatus, 2000); // Initial delay

    } catch (error) {
      console.error('Error in pollMintStatus:', error);
      toast.error('Failed to check purchase status', { id: toastId });
      setIsPurchasing(false);
    }
  };

  // Add state for purchase stage at the component level
  const [purchaseStage, setPurchaseStage] = useState('initial');

  return (
    <button
      onClick={handlePurchase}
      disabled={isPurchasing}
      className={`flex items-center justify-center px-6 py-3 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:from-violet-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {isPurchasing ? (
        <>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
          {purchaseStage === 'payment' && 'Processing Payment...'}
          {purchaseStage === 'minting' && 'Minting NFT...'}
          {purchaseStage === 'initial' && 'Processing...'}
        </>
      ) : (
        <>
          {nft.currency === 'ETH' ? (
            <FaEthereum className="mr-2" />
          ) : (
            <img src="/imx.svg" alt="IMX" className="w-5 h-5 mr-2" />
          )}
          {isPassportConnected ? `Buy for ${nft.price} ${nft.currency}` : 'Connect & Buy'}
        </>
      )}
    </button>
  );
}
